
import { Sparkles, Target, PenTool, LineChart, Rocket, Lightbulb } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import FeatureItem from "@/components/pricing/FeatureItem";
import { Badge } from "@/components/ui/badge";

const BeginnerFeatures = () => {
  return (
    <section className="py-20 px-4 bg-white">
      <div className="container mx-auto max-w-6xl">
        <div className="text-center mb-16">
          <Badge className="px-3 py-1 text-sm bg-blue-600 text-white border-blue-700 mb-4">
            Built for Beginners
          </Badge>
          <h2 className="text-3xl md:text-5xl font-bold mb-6">
            Built to prevent the mistakes that cost beginners money
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Every feature is designed to keep you safe from expensive errors while making Google Ads finally understandable.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <Card className="hover:shadow-md transition-all">
            <CardContent className="p-8">
              <div className="bg-blue-50 rounded-full w-14 h-14 flex items-center justify-center mb-6">
                <Sparkles className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold mb-4">AI-Powered Ad Creation</h3>
              <p className="text-muted-foreground mb-6">
                Our AI writes compelling ad copy based on your business description, making sure your ads stand out.
              </p>
              <ul className="space-y-2">
                <FeatureItem feature="Professional headlines & descriptions" />
                <FeatureItem feature="Multiple ad variations" />
                <FeatureItem feature="Audience-targeted messaging" />
              </ul>
            </CardContent>
          </Card>
          
          <Card className="hover:shadow-md transition-all">
            <CardContent className="p-8">
              <div className="bg-blue-50 rounded-full w-14 h-14 flex items-center justify-center mb-6">
                <Target className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold mb-4">No More Guessing with Keywords</h3>
              <p className="text-muted-foreground mb-6">
                Stop wasting money on wrong keywords. We automatically find the exact words your customers use when they're ready to buy.
              </p>
              <ul className="space-y-2">
                <FeatureItem feature="Proven high-converting keywords" />
                <FeatureItem feature="Automatic low-quality keyword blocking" />
                <FeatureItem feature="Budget-safe keyword recommendations" />
              </ul>
            </CardContent>
          </Card>
          
          <Card className="hover:shadow-md transition-all">
            <CardContent className="p-8">
              <div className="bg-blue-50 rounded-full w-14 h-14 flex items-center justify-center mb-6">
                <PenTool className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold mb-4">Mistake-Proof Campaign Builder</h3>
              <p className="text-muted-foreground mb-6">
                Our step-by-step builder prevents common errors that waste money, with built-in safety checks at every step.
              </p>
              <ul className="space-y-2">
                <FeatureItem feature="Budget protection safeguards" />
                <FeatureItem feature="Quality score optimization" />
                <FeatureItem feature="Automatic bid adjustment limits" />
              </ul>
            </CardContent>
          </Card>
          
          <Card className="hover:shadow-md transition-all">
            <CardContent className="p-8">
              <div className="bg-blue-50 rounded-full w-14 h-14 flex items-center justify-center mb-6">
                <LineChart className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold mb-4">Beginner-Friendly Analytics</h3>
              <p className="text-muted-foreground mb-6">
                Track performance with clear, simple metrics that help you understand what's working.
              </p>
              <ul className="space-y-2">
                <FeatureItem feature="Visual performance charts" />
                <FeatureItem feature="Simple ROI tracking" />
                <FeatureItem feature="Plain-English insights" />
              </ul>
            </CardContent>
          </Card>
          
          <Card className="hover:shadow-md transition-all">
            <CardContent className="p-8">
              <div className="bg-blue-50 rounded-full w-14 h-14 flex items-center justify-center mb-6">
                <Rocket className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold mb-4">One-Click Publishing</h3>
              <p className="text-muted-foreground mb-6">
                Send your campaigns directly to Google Ads with one click. No technical setup required.
              </p>
              <ul className="space-y-2">
                <FeatureItem feature="Direct Google Ads integration" />
                <FeatureItem feature="Automatic account linking" />
                <FeatureItem feature="Instant campaign deployment" />
              </ul>
            </CardContent>
          </Card>
          
          <Card className="hover:shadow-md transition-all">
            <CardContent className="p-8">
              <div className="bg-blue-50 rounded-full w-14 h-14 flex items-center justify-center mb-6">
                <Lightbulb className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold mb-4">Beginner's Learning Center</h3>
              <p className="text-muted-foreground mb-6">
                Learn as you go with our simple guides and tooltips that explain everything in plain English.
              </p>
              <ul className="space-y-2">
                <FeatureItem feature="Video tutorials" />
                <FeatureItem feature="In-app guidance" />
                <FeatureItem feature="Simple best practices" />
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default BeginnerFeatures;
